import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { STORAGE_KEYS, ROUTES } from '../utils/constants';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { BookContainer, BookPage, BookNavigation } from '../components';
import { resetPassword } from '../services/authService';

const SettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser, logOut } = useAuth();

  const handleBack = () => {
    navigate(ROUTES.RECIPES);
  };
  
  // Settings state
  const [imageGeneration, setImageGeneration] = useState<boolean>(
    localStorage.getItem(STORAGE_KEYS.IMAGE_GENERATION) !== 'false'
  );
  const [audioGeneration, setAudioGeneration] = useState<boolean>(
    localStorage.getItem(STORAGE_KEYS.AUDIO_GENERATION) !== 'false'
  );
  const [voiceType, setVoiceType] = useState<string>(
    localStorage.getItem(STORAGE_KEYS.VOICE_TYPE) || 'default'
  );
  const [imageStyle, setImageStyle] = useState<string>(
    localStorage.getItem(STORAGE_KEYS.IMAGE_STYLE) || 'realistic'
  );
  const [imageModel, setImageModel] = useState<string>(
    localStorage.getItem(STORAGE_KEYS.IMAGE_MODEL) || 'gpt-image-1-mini'
  );
  const [textModel, setTextModel] = useState<string>(
    localStorage.getItem(STORAGE_KEYS.TEXT_MODEL) || 'gpt-5-mini'
  );
  const [voiceModel, setVoiceModel] = useState<string>(
    localStorage.getItem(STORAGE_KEYS.VOICE_MODEL) || 'gpt-4o-mini-tts'
  );
  const [autoImage, setAutoImage] = useState<boolean>(
    localStorage.getItem(STORAGE_KEYS.AUTO_IMAGE) === 'true'
  );
  const [autoSpeech, setAutoSpeech] = useState<boolean>(
    localStorage.getItem(STORAGE_KEYS.AUTO_SPEECH) === 'true'
  );
  
  // UI state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState<boolean>(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState<string>('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<boolean>(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [settingsSaved, setSettingsSaved] = useState<boolean>(false);

  useEffect(() => {
    if (currentUser?.email) {
      setResetPasswordEmail(currentUser.email);
    }
  }, [currentUser]);

  const handleImageGenerationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImageGeneration(event.target.checked);
    setSettingsSaved(false);
  };

  const handleAudioGenerationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAudioGeneration(event.target.checked);
    setSettingsSaved(false);
  };

  const handleVoiceTypeChange = (event: SelectChangeEvent<string>) => {
    setVoiceType(event.target.value);
    setSettingsSaved(false);
  };

  const handleImageStyleChange = (event: SelectChangeEvent<string>) => {
    setImageStyle(event.target.value);
    setSettingsSaved(false);
  };

  const handleImageModelChange = (event: SelectChangeEvent<string>) => {
    setImageModel(event.target.value);
    setSettingsSaved(false);
  };

  const handleTextModelChange = (event: SelectChangeEvent<string>) => {
    setTextModel(event.target.value);
    setSettingsSaved(false);
  };

  const handleVoiceModelChange = (event: SelectChangeEvent<string>) => {
    setVoiceModel(event.target.value);
    setSettingsSaved(false);
  };

  const handleAutoImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoImage(event.target.checked);
    setSettingsSaved(false);
  };

  const handleAutoSpeechChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoSpeech(event.target.checked);
    setSettingsSaved(false);
  };

  const handleSaveSettings = () => {
    // Save settings to localStorage
    localStorage.setItem(STORAGE_KEYS.IMAGE_GENERATION, imageGeneration.toString());
    localStorage.setItem(STORAGE_KEYS.AUDIO_GENERATION, audioGeneration.toString());
    localStorage.setItem(STORAGE_KEYS.VOICE_TYPE, voiceType);
    localStorage.setItem(STORAGE_KEYS.IMAGE_STYLE, imageStyle);
    localStorage.setItem(STORAGE_KEYS.IMAGE_MODEL, imageModel);
    localStorage.setItem(STORAGE_KEYS.TEXT_MODEL, textModel);
    localStorage.setItem(STORAGE_KEYS.VOICE_MODEL, voiceModel);
    localStorage.setItem(STORAGE_KEYS.AUTO_IMAGE, autoImage.toString());
    localStorage.setItem(STORAGE_KEYS.AUTO_SPEECH, autoSpeech.toString());
    
    setSettingsSaved(true);
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      setSettingsSaved(false);
    }, 3000);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordEmail) {
      setResetPasswordError('Please enter your email address');
      return;
    }

    try {
      setResetPasswordLoading(true);
      setResetPasswordError(null);
      
      await resetPassword(resetPasswordEmail);
      
      setResetPasswordSuccess(true);
      
      // Close dialog after 3 seconds
      setTimeout(() => {
        setResetPasswordDialogOpen(false);
        setResetPasswordSuccess(false);
      }, 3000);
    } catch (error) {
      setResetPasswordError(error instanceof Error ? error.message : 'Failed to send password reset email');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <BookContainer>
      <Box sx={{ 
        width: '100%', 
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #2c2416 0%, #1a1611 100%)'
          : 'linear-gradient(180deg, #F5E6D3 0%, #E8D5B7 100%)',
        py: 4,
      }}>
        {/* Two-page book spread */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: 0,
          maxWidth: 1200,
          margin: '0 auto',
          minHeight: '80vh',
        }}>
          {/* Left Page - AI Models and Enhancement Settings */}
          <Box sx={{ width: '50%' }}>
            <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.SETTINGS}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Page Header */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <IconButton 
                      onClick={handleBack} 
                      sx={{ 
                        mr: 2,
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 248, 240, 0.8)' 
                          : 'rgba(139, 69, 19, 0.8)',
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <SettingsIcon 
                      sx={{ 
                        fontSize: 28, 
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 248, 240, 0.8)' 
                          : 'rgba(139, 69, 19, 0.8)',
                        mr: 2,
                      }} 
                    />
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 248, 240, 0.9)' 
                          : 'rgba(139, 69, 19, 0.9)',
                      }}
                    >
                      Appendix
                    </Typography>
                  </Box>

                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontStyle: 'italic',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.7)' 
                        : 'rgba(139, 69, 19, 0.7)',
                      mb: 3,
                    }}
                  >
                    Kitchen Settings & Preferences
                  </Typography>

                  {/* Decorative line */}
                  <Box sx={{ 
                    height: 3,
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(90deg, rgba(255, 138, 101, 0.3), transparent)'
                      : 'linear-gradient(90deg, rgba(139, 69, 19, 0.3), transparent)',
                    mb: 4,
                  }} />
                </Box>

                {settingsSaved && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 3,
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.1)' 
                        : 'rgba(139, 69, 19, 0.1)',
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.3)' 
                        : 'rgba(139, 69, 19, 0.3)'}`,
                    }}
                  >
                    Settings saved successfully
                  </Alert>
                )}

              {/* AI Cooking Assistant Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.9)' 
                      : 'rgba(139, 69, 19, 0.9)',
                    mb: 3,
                    borderBottom: `2px dotted ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.3)' 
                      : 'rgba(139, 69, 19, 0.3)'}`,
                    pb: 1,
                  }}
                >
                  AI Cooking Assistant Models
                </Typography>
                
                {/* Text Model */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.8)' 
                        : 'rgba(139, 69, 19, 0.8)',
                      mb: 1,
                    }}
                  >
                    Recipe Text Generation:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={textModel}
                      onChange={handleTextModelChange}
                      displayEmpty
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.3)' 
                            : 'rgba(139, 69, 19, 0.3)'}`,
                        },
                      }}
                    >
                      <MenuItem value="gpt-5-mini">GPT-5 Mini (Recommended)</MenuItem>
                      <MenuItem value="gpt-5">GPT-5</MenuItem>
                      <MenuItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</MenuItem>
                      <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Image Model */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.8)' 
                        : 'rgba(139, 69, 19, 0.8)',
                      mb: 1,
                    }}
                  >
                    Recipe Image Generation:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={imageModel}
                      onChange={handleImageModelChange}
                      displayEmpty
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.3)' 
                            : 'rgba(139, 69, 19, 0.3)'}`,
                        },
                      }}
                    >
                      <MenuItem value="dall-e-3">DALL-E 3 (Best Quality)</MenuItem>
                      <MenuItem value="gpt-image-1-mini">GPT Image Mini</MenuItem>
                      <MenuItem value="gemini-2.0-flash-exp-image-generation">Gemini Flash</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Voice Model */}
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: '"Crimson Text", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.8)' 
                        : 'rgba(139, 69, 19, 0.8)',
                      mb: 1,
                    }}
                  >
                    Recipe Voice Narration:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={voiceModel}
                      onChange={handleVoiceModelChange}
                      displayEmpty
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: `1px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.3)' 
                            : 'rgba(139, 69, 19, 0.3)'}`,
                        },
                      }}
                    >
                      <MenuItem value="tts-1">TTS-1 (Fast)</MenuItem>
                      <MenuItem value="tts-1-hd">TTS-1 HD (High Quality)</MenuItem>
                      <MenuItem value="gpt-4o-mini-tts">GPT-4o Mini TTS</MenuItem>
                      <MenuItem value="gemini-2.5-flash-preview-tts">Gemini Flash TTS</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Recipe Enhancement Section */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 600,
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.9)' 
                      : 'rgba(139, 69, 19, 0.9)',
                    mb: 3,
                    borderBottom: `2px dotted ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.3)' 
                      : 'rgba(139, 69, 19, 0.3)'}`,
                    pb: 1,
                  }}
                >
                  Recipe Enhancement Features
                </Typography>

                {/* Image Generation Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={imageGeneration}
                      onChange={handleImageGenerationChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 138, 101, 0.8)' 
                            : 'rgba(139, 69, 19, 0.8)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 138, 101, 0.5)' 
                            : 'rgba(139, 69, 19, 0.5)',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: '1rem' }}>
                      Generate beautiful food images for recipes
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                {imageGeneration && (
                  <Box sx={{ ml: 4, mb: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: '"Crimson Text", serif',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 248, 240, 0.7)' 
                          : 'rgba(139, 69, 19, 0.7)',
                        mb: 1,
                      }}
                    >
                      Preferred Image Style:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={imageStyle}
                        onChange={handleImageStyleChange}
                        sx={{
                          fontFamily: '"Crimson Text", serif',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: `1px solid ${theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.3)' 
                              : 'rgba(139, 69, 19, 0.3)'}`,
                          },
                        }}
                      >
                        <MenuItem value="realistic">Realistic Photography</MenuItem>
                        <MenuItem value="artistic">Artistic Style</MenuItem>
                        <MenuItem value="professional">Professional Food</MenuItem>
                        <MenuItem value="cartoon">Illustrated</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoImage}
                          onChange={handleAutoImageChange}
                          size="small"
                          sx={{
                            ml: 2,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 138, 101, 0.8)' 
                                : 'rgba(139, 69, 19, 0.8)',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontFamily: '"Crimson Text", serif' }}>
                          Auto-generate for new recipes
                        </Typography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}

                {/* Audio Generation Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={audioGeneration}
                      onChange={handleAudioGenerationChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 138, 101, 0.8)' 
                            : 'rgba(139, 69, 19, 0.8)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 138, 101, 0.5)' 
                            : 'rgba(139, 69, 19, 0.5)',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: '1rem' }}>
                      Add voice narration to cooking steps
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                {audioGeneration && (
                  <Box sx={{ ml: 4, mb: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: '"Crimson Text", serif',
                        color: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 248, 240, 0.7)' 
                          : 'rgba(139, 69, 19, 0.7)',
                        mb: 1,
                      }}
                    >
                      Voice Character:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={voiceType}
                        onChange={handleVoiceTypeChange}
                        sx={{
                          fontFamily: '"Crimson Text", serif',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: `1px solid ${theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.3)' 
                              : 'rgba(139, 69, 19, 0.3)'}`,
                          },
                        }}
                      >
                        <MenuItem value="default">Friendly Chef</MenuItem>
                        <MenuItem value="male">Male Chef</MenuItem>
                        <MenuItem value="female">Female Chef</MenuItem>
                        <MenuItem value="british">British Chef</MenuItem>
                        <MenuItem value="australian">Australian Chef</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoSpeech}
                          onChange={handleAutoSpeechChange}
                          size="small"
                          sx={{
                            ml: 2,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 138, 101, 0.8)' 
                                : 'rgba(139, 69, 19, 0.8)',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontFamily: '"Crimson Text", serif' }}>
                          Auto-generate for new steps
                        </Typography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                )}
              </Box>
              </motion.div>
            </BookPage>
          </Box>

          {/* Right Page - Account Settings and Save Button */}
          <Box sx={{ width: '50%' }}>
            <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.SETTINGS + 1}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                  {/* Account Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.9)' 
                        : 'rgba(139, 69, 19, 0.9)',
                      mb: 3,
                      borderBottom: `2px dotted ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.3)' 
                        : 'rgba(139, 69, 19, 0.3)'}`,
                      pb: 1,
                    }}
                  >
                    About the Chef
                  </Typography>

                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: '"Crimson Text", serif',
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 248, 240, 0.8)' 
                      : 'rgba(139, 69, 19, 0.8)',
                    mb: 3,
                    lineHeight: 1.8,
                  }}
                >
                  <strong>Chef's Email:</strong> {currentUser?.email}
                  <br />
                  <strong>Member since:</strong> {currentUser?.metadata?.creationTime ? 
                    new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'Recently'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setResetPasswordDialogOpen(true)}
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.5)' 
                        : 'rgba(139, 69, 19, 0.5)'}`,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.8)' 
                        : 'rgba(139, 69, 19, 0.8)',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 138, 101, 0.1)' 
                          : 'rgba(139, 69, 19, 0.1)',
                      },
                    }}
                  >
                    Reset Password
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={logOut}
                    startIcon={<LogoutIcon />}
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.5)' 
                        : 'rgba(139, 69, 19, 0.5)'}`,
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.8)' 
                        : 'rgba(139, 69, 19, 0.8)',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 138, 101, 0.1)' 
                          : 'rgba(139, 69, 19, 0.1)',
                      },
                    }}
                  >
                    Sign Out
                  </Button>
                  </Box>
                </Box>

                {/* Save Settings Button */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  pt: 3,
                  borderTop: `1px dotted ${theme.palette.mode === 'dark' 
                    ? 'rgba(255, 248, 240, 0.3)' 
                    : 'rgba(139, 69, 19, 0.3)'}`,
                }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveSettings}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    sx={{
                      fontFamily: '"Crimson Text", serif',
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 138, 101, 0.3)'
                        : 'rgba(139, 69, 19, 0.8)',
                      color: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 248, 240, 0.9)' 
                        : 'rgba(255, 248, 240, 0.95)',
                      border: `2px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 138, 101, 0.5)' 
                        : 'rgba(139, 69, 19, 0.6)'}`,
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.4)'
                          : 'rgba(139, 69, 19, 0.9)',
                      },
                    }}
                  >
                    Save Kitchen Preferences
                  </Button>
                </Box>
              </motion.div>
            </BookPage>
          </Box>
        </Box>
      </Box>

      {/* Reset Password Dialog - keeping original functionality */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => !resetPasswordLoading && setResetPasswordDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(46, 33, 23, 0.95)' 
              : '#FFFEF8',
            backgroundImage: theme.palette.mode === 'dark' ? 'none' : `
              repeating-linear-gradient(
                90deg,
                transparent,
                transparent 2px,
                rgba(139, 69, 19, 0.03) 2px,
                rgba(139, 69, 19, 0.03) 4px
              )
            `,
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", serif' }}>
          Reset Password
        </DialogTitle>
        <DialogContent>
          {resetPasswordSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Password reset email sent successfully
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ fontFamily: '"Crimson Text", serif' }}>
                Enter your email address and we'll send you a link to reset your password.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={resetPasswordEmail}
                onChange={(e) => setResetPasswordEmail(e.target.value)}
                error={!!resetPasswordError}
                helperText={resetPasswordError}
                disabled={resetPasswordLoading}
                sx={{
                  '& .MuiInputLabel-root': {
                    fontFamily: '"Crimson Text", serif',
                  },
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Crimson Text", serif',
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!resetPasswordSuccess && (
            <>
              <Button
                onClick={() => setResetPasswordDialogOpen(false)}
                disabled={resetPasswordLoading}
                sx={{ fontFamily: '"Crimson Text", serif' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resetPasswordLoading}
                variant="contained"
                sx={{ fontFamily: '"Crimson Text", serif' }}
              >
                {resetPasswordLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Book Navigation */}
      <BookNavigation currentPageNumber={PAGE_NUMBERS.SETTINGS} />
    </BookContainer>
  );
};

export default SettingsScreen;
